import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { cx } from "shared/utils";

// Standard 12-key telephone layout. The letters are decoration only, but they
// are what makes a spoken menu ("press 2 for ABC") followable on a screen pad.
const KEYPAD_KEYS: { digit: string; letters: string }[] = [
  { digit: "1", letters: "" },
  { digit: "2", letters: "ABC" },
  { digit: "3", letters: "DEF" },
  { digit: "4", letters: "GHI" },
  { digit: "5", letters: "JKL" },
  { digit: "6", letters: "MNO" },
  { digit: "7", letters: "PQRS" },
  { digit: "8", letters: "TUV" },
  { digit: "9", letters: "WXYZ" },
  { digit: "*", letters: "" },
  { digit: "0", letters: "+" },
  { digit: "#", letters: "" }
];

// The readout is feedback, not an input field — keep the tail of a long entry
// (voicemail PINs, extensions) rather than letting it widen the card.
const MAX_READOUT_DIGITS = 20;

// Keys we accept from the physical keyboard. Matches what the pad renders; "w"
// (the SDK's pause character) is deliberately excluded so a typed word can't
// turn into tones.
const KEYBOARD_DIGIT_RE = /^[0-9*#]$/;

interface CallKeypadProps {
  // Sends a single DTMF tone. Returns false when it never left the SDK (no live
  // call), in which case the digit is kept out of the readout.
  onDigit: (digit: string) => boolean;
  onClose: () => void;
}

/**
 * In-call dial pad (CRM-1190). Any caller-facing IVR in the path — a partner's
 * call-screening/"press 1 to accept" forwarding service, a voicemail PIN, a
 * "press 2 for leasing" menu — is unanswerable without one, which made those
 * calls look broken even though the bridge was up.
 *
 * Rendered only while the call is answered (`CallDisplay.showKeypad`), so the
 * readout state is scoped to one call by mount: hiding the pad or ending the
 * call discards it.
 */
export function CallKeypad({ onDigit, onClose }: CallKeypadProps) {
  const [readout, setReadout] = useState("");
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Take focus on open so the physical keyboard works without clicking a key
  // first. The handler is container-scoped on purpose: a window listener would
  // turn typing elsewhere in the app (the Inbox composer, a search box) into
  // tones on the live call.
  useEffect(() => {
    // preventScroll: the card is height-capped and scrollable, and focusing
    // must not jump it.
    containerRef.current?.focus({ preventScroll: true });
  }, []);

  const press = (digit: string) => {
    if (!onDigit(digit)) return;
    setReadout((prev) => (prev + digit).slice(-MAX_READOUT_DIGITS));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      onClose();
      return;
    }
    if (!KEYBOARD_DIGIT_RE.test(event.key)) return;
    // OS key auto-repeat would machine-gun tones into an IVR from one held
    // key — one keydown, one tone.
    if (event.repeat) return;
    // Space/Enter are left alone so they still activate the focused key.
    event.preventDefault();
    press(event.key);
  };

  return (
    <div
      ref={containerRef}
      role="group"
      aria-label="Dial pad"
      // Focusable but not a tab stop of its own — the keys are.
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      className="mt-6 w-full outline-none"
    >
      <div className="flex items-center gap-2">
        <p
          aria-live="polite"
          className="min-w-0 flex-1 truncate text-left text-sm tracking-[0.25em] text-content-1000 tabular-nums"
        >
          {readout}
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Hide dial pad"
          className="grid size-7 shrink-0 place-items-center rounded-full text-content-300 transition hover:bg-alpha-40 hover:text-content-1000"
        >
          <HugeiconsIcon icon={Cancel01Icon} size={16} />
        </button>
      </div>

      <div className="mt-2 grid grid-cols-3 justify-items-center gap-2">
        {KEYPAD_KEYS.map(({ digit, letters }) => (
          <button
            key={digit}
            type="button"
            onClick={() => press(digit)}
            aria-label={`Send ${digit}`}
            className="relative grid size-14 place-items-center rounded-full bg-alpha-40 transition hover:bg-alpha-100 active:bg-alpha-200"
          >
            <span
              className={cx(
                "font-medium leading-none text-content-1000",
                // The asterisk glyph sits high in its em box — render it larger
                // and nudged down so it lands on the circle's optical center.
                digit === "*" ? "translate-y-[3px] text-2xl" : "text-lg"
              )}
            >
              {digit}
            </span>
            {/* Absolutely positioned so the digit is dead-center on every key
                whether or not it carries letters. */}
            {letters ? (
              <span
                aria-hidden
                className="absolute inset-x-0 bottom-2 text-[10px] leading-none tracking-widest text-content-300"
              >
                {letters}
              </span>
            ) : null}
          </button>
        ))}
      </div>
    </div>
  );
}
